/**
 * @fileoverview Input/output schemas for the critique-prose-flow agent.
 *
 * This agent analyzes prose for rhythm, pacing, and transitions.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import {
  FindingSchema,
  WeaknessSchema,
  EvaluationOutputSchema,
  IssueSeverityEnum,
  type Finding,
  type Weakness,
  type EvaluationOutput,
} from '@/ai/agents/issue-classification';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.chapterContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.styleGuide.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.draftingHistory.schema)
  .merge(AGENT_SCHEMA_OBJECTS.acceptedFindings.schema)
  .merge(AGENT_SCHEMA_OBJECTS.rejectedFindings.schema)
  .merge(AGENT_SCHEMA_OBJECTS.acceptedWeaknesses.schema)
  .merge(AGENT_SCHEMA_OBJECTS.rejectedWeaknesses.schema)
  .merge(AGENT_SCHEMA_OBJECTS.currentStrengths.schema)
  .merge(AGENT_SCHEMA_OBJECTS.genre.schema)
  .merge(AGENT_SCHEMA_OBJECTS.genreGuidance.schema)
  .merge(AGENT_SCHEMA_OBJECTS.pov.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterPovCharacter.schema)
  .merge(AGENT_SCHEMA_OBJECTS.issueClassificationRubric.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

// Re-export shared schemas for use in this agent
export { FindingSchema, WeaknessSchema, IssueSeverityEnum };
export type { Finding, Weakness };

/**
 * Output schema using the unified evaluation output structure.
 */
export const OutputSchema = EvaluationOutputSchema;

export type Output = EvaluationOutput;
